//https://iconduck.com/sets/svg-credit-card-and-payment-icons

import {
  CreditCardClientData,
  PaymentMethods,
  ScannerFundingSource,
  ScannerSubFundingSource,
} from 'types';
import getConfig from 'next/config';
import { IteroPaymentType } from './ITeroOrderPayment.types';

export const getCreditCardLogo = (cardType: string) => {
  const imagePath = '/images/cards/';
  const cardToLogoMapping: {
    [key: string]: string;
  } = {
    dine: `${imagePath}diners.svg`,
    mc: `${imagePath}master.svg`,
    amex: `${imagePath}amex.svg`,
    visa: `${imagePath}visa.svg`,
    jcb: `${imagePath}jcb.svg`,
    disc: `${imagePath}discover.svg`,
    invoice: `${imagePath}invoice.svg`,
    sepa: `${imagePath}sepa.svg`,
    cheque: `${imagePath}cheque.png`,
    wire: `${imagePath}wire.png`,
  };
  return (
    cardToLogoMapping[cardType?.toLowerCase()] ?? `${imagePath}generic.svg`
  );
};

export const reorderCreditCards = (
  creditCards: CreditCardClientData[] | undefined
) => {
  if (creditCards && creditCards.length > 0) {
    const ach = creditCards.filter(card => card.cardType === 'ACH');
    const cards = creditCards.filter(card => card.cardType != 'ACH');
    return [...ach, ...cards];
  } else {
    return creditCards;
  }
};

export const shouldDisplayAddCardOption = (
  paymentType: string,
  tmSelectedScannerFundingSource: ScannerFundingSource,
  tmSelectedScannerSubFundingSource?: ScannerSubFundingSource
) => {
  return (
    (paymentType === 'scanner' &&
      (tmSelectedScannerFundingSource === 'Cash' ||
        tmSelectedScannerFundingSource === 'Cash/Check' ||
        (tmSelectedScannerSubFundingSource !== undefined &&
          [
            '11 Month Deferral',
            'EPT',
            'NT4M',
            'NT6M',
            'NT30',
            'NT60',
            'NT90',
          ].includes(tmSelectedScannerSubFundingSource)))) ||
    paymentType === 'subscription' ||
    paymentType === 'rental'
  );
};

export const getSubFundingSourceByPaymentMethod = (
  tmSlectedScannerFundingSource: ScannerFundingSource,
  tmSelectedScannerSubFundingSource: ScannerSubFundingSource,
  creditCard: CreditCardClientData | undefined,
  userSelectedScannerSubFundingSource: ScannerSubFundingSource
): ScannerSubFundingSource => {
  if (tmSlectedScannerFundingSource === 'Cash') {
    if (creditCard) {
      if (creditCard.cardType === 'ACH') {
        return 'ACH (Account and Routing number)';
      } else {
        return 'Credit Card (Full Payment)';
      }
    } else {
      return userSelectedScannerSubFundingSource;
    }
  }

  if (tmSlectedScannerFundingSource === 'Credit') {
    return tmSelectedScannerSubFundingSource;
  }

  if (tmSlectedScannerFundingSource === '3rd Party Financing') {
    return '3rd Party Financing';
  }
  return tmSlectedScannerFundingSource;
};

export const getPaymentMethodsByFundingSource = (
  tmSelectedScannerFundingSource: ScannerFundingSource,
  creditCardsWithACH: CreditCardClientData[],
  tmSelectedScannerSubFundingSource?: ScannerSubFundingSource,
  financingCompany?: string
) => {
  const paymentMethods: {
    cards: CreditCardClientData[];
    others: { label: string; value: ScannerSubFundingSource; logo?: string }[];
  } = {
    cards: [],
    others: [],
  };
  if (tmSelectedScannerFundingSource === 'Cash') {
    paymentMethods.cards = creditCardsWithACH;
    paymentMethods.others = [
      {
        label: 'Check (Bank/Customer)',
        value: 'Check (Bank/Customer)',
        logo: 'cheque',
      },
      {
        label: 'Wire (Bank/Customer)',
        value: 'Wire (Bank/Customer)',
        logo: 'wire',
      },
    ];
  }

  if (
    tmSelectedScannerSubFundingSource &&
    tmSelectedScannerFundingSource === 'Credit' &&
    [
      '11 Month Deferral',
      'EPT',
      'NT4M',
      'NT6M',
      'NT30',
      'NT60',
      'NT90',
    ].includes(tmSelectedScannerSubFundingSource)
  ) {
    paymentMethods.cards = creditCardsWithACH;
  }

  if (tmSelectedScannerFundingSource === '3rd Party Financing') {
    paymentMethods.others = [
      {
        label: `3rd Party Financing (${financingCompany})`,
        value: '3rd Party Financing',
      },
    ];
  }
  return paymentMethods;
};

export const getEuPaymentMethodsByFundingSource = (
  tmSelectedScannerFundingSource: ScannerFundingSource,
  creditCardsWithACH: CreditCardClientData[]
) => {
  const paymentMethods: {
    cards: CreditCardClientData[];
    others: { label: string; value: ScannerSubFundingSource; logo?: string }[];
  } = {
    cards: [],
    others: [],
  };

  if (
    tmSelectedScannerFundingSource === 'Cash' ||
    tmSelectedScannerFundingSource === 'Credit' ||
    tmSelectedScannerFundingSource === 'Cash/Check'
  ) {
    paymentMethods.cards = creditCardsWithACH;
  }

  if (tmSelectedScannerFundingSource === 'Financing') {
    paymentMethods.others = [
      {
        label: 'Financing',
        value: 'Financing',
      },
    ];
  }
  return paymentMethods;
};

export const shouldDisableSubmitPayment = (
  paymentType: IteroPaymentType,
  cards: CreditCardClientData[],
  selectedCreditCard?: CreditCardClientData,
  selectedSubfundingSource?: ScannerSubFundingSource
): boolean => {
  const requiresCreditCard =
    paymentType === 'subscription' ||
    paymentType === 'rental' ||
    (paymentType === 'scanner' &&
      (selectedSubfundingSource == null ||
        [
          'ACH (Account and Routing number)',
          'Credit Card (Full Payment)',
          '11 Month Deferral',
          'EPT',
          'NT4M',
          'NT6M',
          'NT30',
          'NT60',
          'NT90',
        ].includes(selectedSubfundingSource)));

  return requiresCreditCard && (!selectedCreditCard || cards.length === 0);
};

export const shouldDisableSubmitPaymentEU = (
  paymentType: IteroPaymentType,
  cards: CreditCardClientData[],
  selectedCreditCard?: CreditCardClientData,
  tmSelectedScannerFundingSource?: ScannerFundingSource
): boolean => {
  const requiresCreditCard =
    paymentType === 'subscription' &&
    (!tmSelectedScannerFundingSource ||
      ['Cash/Check', 'Financing'].includes(tmSelectedScannerFundingSource));

  return requiresCreditCard && (!selectedCreditCard || cards.length === 0);
};

export const getSubFundingSourceForSalesforce = (
  fundingSource: ScannerFundingSource,
  subfundingSource: ScannerSubFundingSource
): ScannerSubFundingSource => {
  if (
    fundingSource === '3rd Party Financing' ||
    fundingSource === 'Financing'
  ) {
    return '';
  } else {
    return subfundingSource;
  }
};
